import { type UseModalState } from "@clipboard-health/ui-react";
import { Box, Dialog as MuiDialog, Fade, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { forwardRef, type ReactNode } from "react";

import { DialogHeader } from "./DialogHeader";
import { useModalsContainerContext } from "./useModalsContainerContext";

/**
 * iOS-style transition configuration for dialogs
 *
 * Dialogs use fade transitions which are faster than slide animations.
 */
const DIALOG_TIMING = {
  enter: 300,
  exit: 250,
};

export interface DialogProps {
  /**
   * The content of the dialog body
   */
  children: React.ReactNode;
  /**
   * Dialog state from useModalState hook
   */
  modalState: UseModalState;
  /**
   * Width of the dialog in pixels
   * @default 400
   */
  width?: number;
  /**
   * Optional title for the dialog header
   */
  title?: string;
  /**
   * Show close button in header (only shown if title is provided)
   */
  showCloseButton?: boolean;
  /**
   * Optional footer content (sticky at bottom)
   */
  footerContent?: ReactNode;
  /**
   * Additional styles for the dialog paper
   */
  sx?: SxProps<Theme>;
}

interface FadeTransitionProps {
  children: React.ReactElement;
  in?: boolean;
}

/**
 * Transition component for the Dialog using Fade
 */
const FadeTransition = forwardRef<HTMLDivElement, FadeTransitionProps>((props, ref) => (
  <Fade
    ref={ref}
    {...props}
    timeout={{
      enter: DIALOG_TIMING.enter,
      exit: DIALOG_TIMING.exit,
    }}
  />
));

FadeTransition.displayName = "FadeTransition";

/**
 * Dialog component for centered dialogs.
 *
 * Features:
 * - Fixed width of 400px (matching Berlin design system)
 * - Optional header with title and close button
 * - Optional sticky footer for actions
 * - iOS-style fade animation
 * - Container scoping support via ModalsContainerProvider
 */
export function Dialog(props: DialogProps) {
  const {
    children,
    modalState,
    width = 400,
    title,
    showCloseButton = true,
    footerContent,
    sx,
  } = props;

  const { containerRef } = useModalsContainerContext();
  const container = containerRef?.current ?? null;

  const hasHeader = Boolean(title);

  return (
    <MuiDialog
      open={modalState.modalIsOpen}
      TransitionComponent={FadeTransition}
      container={container ?? undefined}
      sx={mergeSxProps(
        (theme: Theme) => ({
          ...(container && {
            position: "absolute",
            zIndex: theme.zIndex.modal,
          }),
          "& .MuiDialog-paper": {
            width,
            maxWidth: "calc(100% - 32px)",
            margin: theme.spacing(4),
            borderRadius: theme.borderRadius?.xLarge,
            boxShadow: theme.shadow?.modal,
            backgroundColor: theme.palette.background?.standout,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            ...(container && {
              position: "absolute",
            }),
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            ...(container && {
              position: "absolute",
            }),
          },
        }),
        sx
      )}
      onClose={modalState.closeModal}
    >
      {hasHeader && (
        <DialogHeader
          title={title ?? ""}
          showCloseButton={showCloseButton}
          onClose={modalState.closeModal}
        />
      )}

      <Box
        sx={(theme) => ({
          padding: theme.spacing(5),
          overflow: "auto",
          flex: 1,
        })}
      >
        {children}
      </Box>

      {footerContent && (
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
            flexShrink: 0,
          })}
        >
          {footerContent}
        </Box>
      )}
    </MuiDialog>
  );
}
